// i18n version: 2026-04-18-v3 (cache-bust + boot guard)
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import enTranslations from '@/i18n/en';
import esTranslations from '@/i18n/es';

// Boot-time validation: ensure translations loaded before rendering tree
const I18N_READY =
  enTranslations &&
  typeof enTranslations === 'object' &&
  Object.keys(enTranslations).length > 100 &&
  esTranslations &&
  typeof esTranslations === 'object' &&
  Object.keys(esTranslations).length > 100;

type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

const allTranslations: Record<Language, Record<string, string>> = {
  en: enTranslations,
  es: esTranslations,
};

export const tGlobal = (key: string): string => {
  try {
    const saved = typeof window !== 'undefined' ? (localStorage.getItem('language') as Language) : 'en';
    const lang = saved && ['en', 'es'].includes(saved) ? saved : 'en';
    const current = allTranslations[lang] || allTranslations.en;
    return current[key] || allTranslations.en[key] || key;
  } catch {
    return key;
  }
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('language') as Language;
      return savedLanguage && ['en', 'es'].includes(savedLanguage) ? savedLanguage : 'en';
    }
    return 'en';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const t = useCallback((key: string): string => {
    try {
      const translations = allTranslations[language];
      if (!translations) {
        const fallback = enTranslations[key];
        return fallback !== undefined && fallback !== null && fallback !== '' ? String(fallback) : key;
      }
      const translation = translations[key];
      if (translation === undefined || translation === null || translation === '') {
        const enFallback = enTranslations[key];
        if (enFallback !== undefined && enFallback !== null && enFallback !== '') {
          return String(enFallback);
        }
        if (import.meta.env.DEV && key.includes('.')) {
          // eslint-disable-next-line no-console
          console.warn(`[i18n] Missing translation for key: "${key}"`);
        }
        return key;
      }
      return String(translation);
    } catch {
      return key;
    }
  }, [language]);

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t
  };

  if (!I18N_READY) {
    return <div style={{ minHeight: '100vh', background: 'hsl(var(--background, 0 0% 100%))' }} />;
  }

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
